import React, { Component, Context, useContext, useState, useEffect, forwardRef } from "react";
// import { connect } from "react-redux";
import clsx from "clsx";
import get from "lodash.get";
import { ITooltipParams, ColDef } from "ag-grid-community";
import { ISelectFormField, ClientFirebase } from "../../laco-common";
import AppContext, { IAppContext } from "../../contexts/app/AppContext";
import { firewireApp } from "../../firewire";
import { getCollection } from "../../laco-common/base/firestore-utils";
import { getCMSColsConfig } from "./columns/utils";
import { FormattedViewContext } from "./colDef";
import { getFormattedDataForView } from "./data-formatters";
import { UIDisplayFields } from "./UIFormattedView";
import { UINoData } from "../ui/UIComponents";
import { IImage } from "../../models/Image";
import ImageView from "../ui/Media/ImageView";
import CMSRoleContext, { ICMSRoleContext } from "../../admin/contexts/cms-role/CMSRole";
import { FileType } from "../../laco-common/base/models/IFile";
import "./CustomTooltip.scss";

export enum CustomTooltipDisplayMode {
	Content = "Content",
	DrillToCollection = "DrillToCollection",
	Image = "Image",
}

export type CustomTooltipParams = {
	field?: ISelectFormField;
	useFirewire?: boolean;
	className?: string;
	showDrillValue?: boolean;
	tooltipDisplayMode?: CustomTooltipDisplayMode;
	renderContent?: (params) => React.ReactNode | string;
	findDrillItem?: (item, props) => boolean;
};

export const CustomTooltip: React.FC<ITooltipParams & CustomTooltipParams> & {
	collectionDataMap: { [collectionName: string]: { data: any[]; columnDefs: ColDef[] } };
} = (props: ITooltipParams & CustomTooltipParams) => {
	const collectionName: string = props.field?.collectionName;
	const appContext: IAppContext = useContext<IAppContext>(AppContext);
	const cmsRoleContext: ICMSRoleContext = useContext<ICMSRoleContext>(CMSRoleContext);
	const [loading, setLoading] = useState<boolean>(true);
	const [drillItem, setDrillItem] = useState<any>(null);
	const [refresh, setRefresh] = useState<number>(0);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>(CustomTooltip.collectionDataMap[collectionName]?.columnDefs);
	const [image, setImage] = useState<IImage>(null);

	const displayMode: CustomTooltipDisplayMode = props.tooltipDisplayMode;
	// const clientFirebase: ClientFirebase = props.useFirewire
	// 	? (firewireApp as any)
	// 	: appContext.firebase.clientFirebase;
	const data = props.api?.getDisplayedRowAtIndex(props?.rowIndex)?.data;
	const itemName: string = props.field.label;

	const getFirebase = (): ClientFirebase => {
		return props.useFirewire ? (firewireApp as any) : appContext.firebase.clientFirebase;
	};

	useEffect(() => {
		console.log("CustomTooltip.componentDidMount() props:", props);
		if (!props.field || !data) return;
		switch (displayMode) {
			case CustomTooltipDisplayMode.DrillToCollection: {
				initCollection();
				break;
			}
			case CustomTooltipDisplayMode.Content: {
				setLoading(false);
				break;
			}
			case CustomTooltipDisplayMode.Image: {
				initImage();
				break;
			}
		}
		if (!props.showDrillValue) (props as any).reactContainer.classList.add("custom-tooltip");
	}, []);

	if (!data) return null;
	if (props.location === "header") return null;

	const initCollection = async () => {
		if (!collectionName) return;
		console.log("CustomTooltip.initCollection()");
		if (!CustomTooltip.collectionDataMap[collectionName]) {
			CustomTooltip.collectionDataMap[collectionName] = { data: null, columnDefs: null };
		}
		if (!CustomTooltip.collectionDataMap[collectionName]?.data) {
			await loadCollectionData(collectionName);
		}
		if (!CustomTooltip.collectionDataMap[collectionName]?.columnDefs) {
			await loadCollectionColDefs(collectionName);
			// setColumnDefs(CustomTooltip.collectionDataMap[collectionName]?.columnDefs);
		}
		const iterator = props.findDrillItem
			? (item) => props.findDrillItem(item, props)
			: (item) => item.id === data[props.field?.id];
		const itemFound = CustomTooltip.collectionDataMap[collectionName]?.data.find(iterator);
		setDrillItem(itemFound);
		setLoading(false);
	};

	const initImage = async () => {
		console.log("CustomTooltip.initImage()");
		const imageUrlField: string = props.field.id;
		const imageUrl: string = data[imageUrlField];
		const image: IImage = imageUrl ? { filename: imageUrl, fileType: FileType.IMAGE, url: imageUrl } : null;
		setImage(image);
		setLoading(false);
	};

	const loadCollectionData = async (collectionName) => {
		console.log("CustomTooltip.loadCollectionData(): collectionName:", collectionName);
		const clientFirebase: ClientFirebase = getFirebase();
		const data = await getCollection(clientFirebase, collectionName);
		CustomTooltip.collectionDataMap[collectionName].data = data;
	};

	const loadCollectionColDefs = async (collectionName: string) => {
		console.log("loadCollectionColDefs");
		const clientFirebase: ClientFirebase = getFirebase();
		const columnDefs: ColDef[] = await getCMSColsConfig(
			clientFirebase,
			collectionName,
			cmsRoleContext,
			CustomTooltip.collectionDataMap[collectionName].data,
			true
		);
		CustomTooltip.collectionDataMap[collectionName].columnDefs = columnDefs;
		console.log("columnDefs loaded:", collectionName, columnDefs);
		setColumnDefs(columnDefs);
		setRefresh(refresh + 1);
	};

	if (props.location === "header" || !props.value) return null;

	console.log("displayMode:", displayMode);
	console.log("drillItem, columnDefs:", drillItem, columnDefs);

	return (
		<div
			className={clsx(
				"custom-tooltip-inner",
				loading && "progress-text",
				props.className,
				displayMode === CustomTooltipDisplayMode.Image && "tooltip-image",
				displayMode === CustomTooltipDisplayMode.Content && "tooltip-content"
			)}
		>
			{!loading && (
				<div>
					{displayMode === CustomTooltipDisplayMode.DrillToCollection && (
						<>
							{!!drillItem && !!columnDefs?.length && (
								<FormattedViewContext.Provider
									value={{
										viewData: getFormattedDataForView<any>(columnDefs, drillItem),
									}}
									key={`${drillItem?.id}-${columnDefs?.length}-${refresh}`}
								>
									<UIDisplayFields cols={columnDefs as any} omitFieldIfEmpty={true} />
								</FormattedViewContext.Provider>
							)}
							{!drillItem && <UINoData msg={itemName ? `${itemName} not found` : undefined} />}
						</>
					)}
					{displayMode === CustomTooltipDisplayMode.Image && (
						<>
							{!!image && <ImageView image={image} viewWidth={250} viewHeight={250} />}
							{!image && <UINoData msg={itemName ? `${itemName} not found` : undefined} />}
						</>
					)}
					{displayMode === CustomTooltipDisplayMode.Content &&
						!!props.renderContent &&
						props.renderContent({ data, value: get(data, props.field?.id) })}
				</div>
			)}
		</div>
	);
};

CustomTooltip.collectionDataMap = {};
export default CustomTooltip;
